import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { 
  Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem, 
  Autocomplete, Chip, IconButton, CircularProgress, Paper, Typography,
  Accordion, AccordionSummary, AccordionDetails, Box, Card, CardMedia,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { 
  createOpportunity, updateOpportunity, getOpportunityById, 
  getJoinedGreenUsers, uploadOpportunityAttachment, deleteOpportunityAttachment, 
  setStoreData, getOpportunityTags, createOpportunityTag, getClientLocations
} from '../../Redux/actions';
import { 
  inputLabelFocusedStyle, selectFocusedStyle, buttonStyles, 
  datePickerSlotProps, datePickerTextFieldStyle 
} from '../../theme/formStyles';

const themedTextFieldSx = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#008080' }
};

const themedAutocompleteSx = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#008080' }
};

export default function ManageOpportunity() {
  const { client_id, id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [greenUsers, setGreenUsers] = useState([]);
  const [locations, setLocations] = useState([]);

  const [form, setForm] = useState({
    opportunity_name: '',
    opportunity_name_key: '',
    description: '',
    segment: '',
    location_id: '',
    bd_owners: [],
    qualified_lead_date: '',
    qualified_lead_type: '',
    qualified_lead_comment: '',
    feasibility_date: '',
    feasibility_visit_report: '',
    feasibility_comment: '',
    won_lost_status: '',
    won_lost_date: '',
    po_value_ex_gst: '',
    won_lost_comment: '',
    status: 'ACTIVE',
  });

  const modeOfCommunicationOptions = [
    'In-Person Meeting',
    'Telephone/Voice Call',
    'Email Communication',
    'Teams VC',
    'Instant Messaging/Chat',
  ];

  const [proposals, setProposals] = useState([
    { proposal_date: '', proposal_value: '', proposal_type: '', mode_of_communication: '', comment: '' }
  ]);

  const [negotiations, setNegotiations] = useState([
    { negotiation_date: '', negotiated_value: '', negotiation_mode_of_communication: '', comment: '' }
  ]);

  const [errors, setErrors] = useState({});

  const [attachments, setAttachments] = useState({
    qualified_lead: [],
    feasibility: [],
    proposal: [],
    negotiation: [],
    won_lost: []
  });

  const [deletedAttachments, setDeletedAttachments] = useState([]);
  const [uploadingStage, setUploadingStage] = useState(null);
  const [previewModal, setPreviewModal] = useState({ open: false, attachment: null });
  
  const [opportunityNameTags, setOpportunityNameTags] = useState([]);
  const [openAddNameDialog, setOpenAddNameDialog] = useState(false);
  const [customNameKey, setCustomNameKey] = useState('');
  const [customNameText, setCustomNameText] = useState('');

  const segmentOptions = [
    'Residential Real Estate',
    'Commercial Real Estate',
    'Hospitality',
    'Healthcare',
    'Industrial & Manufacturing'
  ];

  const qualifiedLeadTypes = ['INBOUND', 'OUTBOUND', 'BUSINESS PARTNER'];

  useEffect(() => {
    fetchGreenUsers();
    fetchOpportunityTags();
    fetchLocations();
    if (id) {
      fetchOpportunity();
    }
  }, [id]);

  const fetchLocations = () => {
    dispatch(getClientLocations(client_id)).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        setLocations(res.data.data || []);
      }
    });
  };

  const fetchOpportunityTags = () => {
    dispatch(getOpportunityTags()).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        setOpportunityNameTags(res.data.data);
      }
    });
  };

  const handleOpenAddNameDialog = () => {
    setOpenAddNameDialog(true);
  };

  const handleCloseAddNameDialog = () => {
    setOpenAddNameDialog(false);
    setCustomNameKey('');
    setCustomNameText('');
  };

  const handleAddCustomName = () => {
    if (!customNameText) return;
    setForm(prev => ({ 
      ...prev, 
      opportunity_name: customNameText,
      opportunity_name_key: customNameKey || 'OPPORTUNITY_NAME'
    }));
    // Add to local suggestions list for immediate availability
    setOpportunityNameTags(prev => {
      if (prev.find(t => t.tag_text === customNameText)) return prev;
      return [...prev, { tag_text: customNameText, tag_key: customNameKey || 'OPPORTUNITY_NAME' }];
    });
    handleCloseAddNameDialog();
  };

  const fetchGreenUsers = () => {
    dispatch(getJoinedGreenUsers()).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        let users = res.data.allData || res.data.data || [];
        users = users.filter((u) => {
          const email = (u.user_id || '').toLowerCase();
          return email.includes('@greenvironment');
        });
        setGreenUsers(users);
      }
    });
  };

  const fetchOpportunity = () => {
    setLoading(true);
    dispatch(getOpportunityById(id)).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        const data = res.data.data;
        setForm({
          opportunity_name: data.opportunity_name || '',
          description: data.description || '',
          segment: data.segment || '',
          location_id: data.location_id || '',
          bd_owners: data.bd_owners || [],
          qualified_lead_date: data.qualified_lead_date || '',
          qualified_lead_type: data.qualified_lead_type || '',
          qualified_lead_comment: data.qualified_lead_comment || '',
          feasibility_date: data.feasibility_date || '',
          feasibility_visit_report: data.feasibility_visit_report || '',
          feasibility_comment: data.feasibility_comment || '',
          won_lost_status: ['Won', 'Lost', 'Hold'].includes(data.status) ? data.status : '',
          won_lost_date: data.won_lost_date || '',
          po_value_ex_gst: data.po_value_ex_gst || '',
          won_lost_comment: data.won_lost_comment || '',
          status: data.status || 'ACTIVE',
        });
        if (data.proposals && data.proposals.length > 0) {
          setProposals(data.proposals);
        }
        if (data.negotiations && data.negotiations.length > 0) {
          setNegotiations(data.negotiations);
        }
        
        // Load existing attachments
        if (data.attachments && data.attachments.length > 0) {
          const groupedAttachments = {
            qualified_lead: [],
            feasibility: [],
            proposal: [],
            negotiation: [],
            won_lost: []
          };
          
          data.attachments.forEach(att => {
            if (groupedAttachments[att.stage]) {
              groupedAttachments[att.stage].push(att);
            }
          });
          
          setAttachments(groupedAttachments);
        }
      }
      setLoading(false);
    });
  };

  const handleChange = (field, value) => {
    if (field === 'won_lost_status' && value !== 'Won') {
      setForm(prev => ({ ...prev, won_lost_status: value, po_value_ex_gst: '' }));
      return;
    }
    setForm({ ...form, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }

    // Auto-populate visit report when feasibility date is selected
    if (field === 'feasibility_date' && value) {
      const visitReport = `Visit Report - ${form.opportunity_name} - ${dayjs(value).format('DD/MM/YYYY')}`;
      setForm(prev => ({ ...prev, feasibility_visit_report: visitReport }));
    }
  };

  const addProposal = () => {
    setProposals([...proposals, { proposal_date: '', proposal_value: '', proposal_type: '', comment: '' }]);
  };

  const removeProposal = (index) => {
    setProposals(proposals.filter((_, i) => i !== index));
  };

  const updateProposal = (index, field, value) => {
    const updated = [...proposals];
    updated[index][field] = value;
    setProposals(updated);
    
    // Clear error for this field if it is now filled
    if (value && errors.proposals && errors.proposals[index] && errors.proposals[index][field]) {
      const newPropsErrors = [...errors.proposals];
      const fieldErr = { ...newPropsErrors[index] };
      delete fieldErr[field];
      
      if (Object.keys(fieldErr).length === 0) {
        newPropsErrors[index] = null;
      } else {
        newPropsErrors[index] = fieldErr;
      }
      
      setErrors(prev => ({ ...prev, proposals: newPropsErrors }));
    }
  };

  const addNegotiation = () => {
    setNegotiations([...negotiations, { negotiation_date: '', negotiated_value: '', negotiation_mode_of_communication: '', comment: '' }]);
  };

  const removeNegotiation = (index) => {
    setNegotiations(negotiations.filter((_, i) => i !== index));
  };

  const updateNegotiation = (index, field, value) => {
    const updated = [...negotiations];
    updated[index][field] = value;
    setNegotiations(updated);
  };

  const handleFileUpload = async (stage, event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Store files temporarily instead of uploading immediately
    const newFiles = files.map(file => ({
      file,
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      preview: URL.createObjectURL(file)
    }));
    
    setAttachments(prev => ({
      ...prev,
      [stage]: [...prev[stage], ...newFiles]
    }));
  };

  const removeAttachment = (stage, index) => {
    const attachment = attachments[stage][index];
    if (attachment?.id && typeof attachment.id === 'string') {
      // Defer deletion until form submission
      setDeletedAttachments(prev => [...prev, attachment.id]);
    }
    setAttachments(prev => ({
      ...prev,
      [stage]: prev[stage].filter((_, i) => i !== index)
    }));
  };

  const handleOpenPreview = (attachment) => {
    setPreviewModal({ open: true, attachment });
  };

  const handleClosePreview = () => {
    setPreviewModal({ open: false, attachment: null });
  };

  const renderFilePreview = (attachment) => {
    const isImage = attachment.file_type?.startsWith('image/');
    const isPDF = attachment.file_type === 'application/pdf';

    if (isImage) {
      return (
        <CardMedia
          component="img"
          height="100"
          image={attachment.preview || attachment.file_path}
          alt={attachment.file_name}
          sx={{ objectFit: 'cover' }}
        />
      );
    } else if (isPDF) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100, bgcolor: '#f5f5f5' }}>
          <InsertDriveFileIcon sx={{ fontSize: 48, color: '#d32f2f' }} />
        </Box>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100, bgcolor: '#f5f5f5' }}>
          <InsertDriveFileIcon sx={{ fontSize: 48, color: '#666' }} />
        </Box>
      );
    }
  };

  const validate = () => {
    const err = {};
    if (!form.opportunity_name) err.opportunity_name = 'Opportunity name is required';
    if (!form.segment) err.segment = 'Segment is required';
    if (form.bd_owners.length === 0) err.bd_owners = 'At least one BD Owner/SPOC is required';

    // Sequence Validation
    const hasStage1 = form.qualified_lead_date || form.qualified_lead_comment || attachments.qualified_lead?.length > 0;
    const hasStage2 = form.feasibility_date || form.feasibility_comment || form.feasibility_visit_report || attachments.feasibility?.length > 0;
    const hasStage3 = proposals.some(p => p.proposal_date || p.proposal_value || p.proposal_type || p.comment);
    const hasStage4 = negotiations.some(n => n.negotiation_date || n.negotiated_value || n.comment) || attachments.negotiation?.length > 0;
    const hasStage5 = form.won_lost_status || form.won_lost_date || form.po_value_ex_gst || form.won_lost_comment || attachments.won_lost?.length > 0;

    // Stage Sequencing Validation
    if (hasStage5 && !hasStage4) err.stages = 'Please fill Stage 4 before submitting Stage 5';
    else if (hasStage4 && !hasStage3) err.stages = 'Please fill Stage 3 before submitting Stage 4';
    else if (hasStage3 && !hasStage2) err.stages = 'Please fill Stage 2 before submitting Stage 3';
    else if (hasStage2 && !hasStage1) err.stages = 'Please fill Stage 1 before submitting Stage 2';

    // Negotiation Field Validation
    const negotiationErrors = negotiations.map((n) => {
      const nErr = {};
      const hasAny = n.negotiation_date || n.negotiated_value || n.comment;
      if (hasAny && !n.negotiated_value) {
        nErr.negotiated_value = 'Negotiated value is required';
      }
      return Object.keys(nErr).length > 0 ? nErr : null;
    });
    if (negotiationErrors.some(e => e !== null)) {
      err.negotiations = negotiationErrors;
    }

    // Proposal Field Validation
    const proposalErrors = proposals.map((p, index) => {
      const pErr = {};
      const hasAny = p.proposal_date || p.proposal_value || p.proposal_type || p.comment;
      if (hasAny && !p.proposal_value) {
        pErr.proposal_value = 'Proposal value is required';
      }
      return Object.keys(pErr).length > 0 ? pErr : null;
    });

    if (proposalErrors.some(e => e !== null)) {
      err.proposals = proposalErrors;
    }

    setErrors(err);

    if (err.stages) {
      dispatch(setStoreData('alertMessage', {
        msg: err.stages,
        type: 'fail',
        handleClose: () => dispatch(setStoreData('alertMessage', null))
      }));
    }

    return Object.keys(err).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    let calculatedStatus = 'Qualified Lead';
    const hasStage5Data = form.won_lost_status || form.won_lost_date || form.po_value_ex_gst || form.won_lost_comment || (attachments.won_lost && attachments.won_lost.length > 0);
    const hasStage4Data = negotiations.some(n => n.negotiation_date || n.negotiated_value || n.comment) || (attachments.negotiation && attachments.negotiation.length > 0);
    const hasStage3Data = proposals.some(p => p.proposal_date || p.proposal_value || p.proposal_type || p.comment);
    const hasStage2Data = form.feasibility_date || form.feasibility_comment || form.feasibility_visit_report || (attachments.feasibility && attachments.feasibility.length > 0);

    if (hasStage5Data) {
      calculatedStatus = form.won_lost_status || 'Lost';
    } else if (hasStage4Data) {
      calculatedStatus = 'Negotiation';
    } else if (hasStage3Data) {
      calculatedStatus = 'Proposal';
    } else if (hasStage2Data) {
      calculatedStatus = 'Feasibility Study';
    }

    const payload = {
      ...form,
      location_id: form.location_id || null,
      status: calculatedStatus,
      client_id,
      proposals: proposals.filter(p => p.proposal_date || p.proposal_value || p.proposal_type || p.comment),
      negotiations: negotiations.filter(n => n.negotiation_date || n.negotiated_value || n.comment),
    };

    setLoading(true);
    const action = id ? updateOpportunity(id, payload) : createOpportunity(payload);
    
    dispatch(action).then(async (res) => {
      // Create endpoint returns 201 Created from backend normally, Update returns 200
      if ((res?.status === 200 || res?.status === 201) && res.data?.success) {
        const targetId = id || res.data.data.id;
        let hasUploadErrors = false;

        // Process deferred deletions
        for (const deleteId of deletedAttachments) {
          try {
            await dispatch(deleteOpportunityAttachment(deleteId));
          } catch (error) {
            console.error('Failed to delete attachment:', error);
            hasUploadErrors = true;
          }
        }

        // Process new file uploads
        for (const stage of Object.keys(attachments)) {
          const stageFiles = attachments[stage].filter(att => att.file);
          
          for (const newFile of stageFiles) {
            const formData = new FormData();
            formData.append('file', newFile.file);
            formData.append('stage', stage);

            try {
              await dispatch(uploadOpportunityAttachment(targetId, formData));
            } catch (error) {
              console.error(`Failed to upload attachment for stage ${stage}:`, error);
              hasUploadErrors = true;
            }
          }
        }

        setLoading(false);
        const successMsg = res.data.message || (id ? 'Opportunity updated successfully' : 'Opportunity created successfully');
        updateResponse(res, successMsg + (hasUploadErrors ? ' (some attachments failed)' : ''));
      } else {
        setLoading(false);
        updateResponse(res, res?.data?.message);
      }
    });
  };

  const updateResponse = (resp, message) => {
    const res = resp && resp.data;
    let msg = res?.message || message;
    let type = 'fail';
    if (res?.success) {
      msg = message;
      type = 'success';
    }
    dispatch(setStoreData('alertMessage', {
      msg,
      type,
      handleClose: () => handleCloseDialog(type),
    }));
  };

  const handleCloseDialog = (type) => {
    dispatch(setStoreData('alertMessage', null));
    if (type === 'success') {
      navigate(`/client/${client_id}/settings/opportunity`);
    }
  };

  const handleBack = () => {
    navigate(`/client/${client_id}/settings/opportunity`);
  };

  const ThemedDatePicker = ({ label, value, onChange }) => (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <DatePicker
        label={label}
        format="DD/MM/YYYY"
        value={value ? dayjs(value) : null}
        onChange={(newVal) => onChange(newVal ? newVal.format('YYYY-MM-DD') : '')}
        slotProps={datePickerSlotProps({ fullWidth: true, size: 'medium' })}
        sx={{ width: '100%' }}
      />
    </LocalizationProvider>
  );

  const FileUploadSection = ({ stage }) => (
    <Grid item xs={12}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <input
          accept="image/*,application/pdf,.doc,.docx"
          style={{ display: 'none' }}
          id={`file-upload-${stage}`}
          type="file"
          multiple
          onChange={(e) => handleFileUpload(stage, e)}
        />
        <label htmlFor={`file-upload-${stage}`}>
          <Button
            variant="outlined"
            component="span"
            startIcon={uploadingStage === stage ? <CircularProgress size={16} sx={{ color: '#008080' }} /> : <AttachFileIcon />}
            disabled={uploadingStage === stage}
            size="small"
            sx={buttonStyles.outlined}
          >
            {uploadingStage === stage ? 'Uploading...' : 'Attach Files'}
          </Button>
        </label>
      </Box>
      {attachments[stage].length > 0 && (
        <Grid container spacing={1} sx={{ mt: 1 }}>
          {attachments[stage].map((attachment, index) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
              <Card sx={{ position: 'relative', border: '1px solid #e0e0e0' }}>
                <Box onClick={() => handleOpenPreview(attachment)} sx={{ cursor: 'pointer' }}>
                  {renderFilePreview(attachment)}
                </Box>
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                  onClick={() => removeAttachment(stage, index)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" sx={{ display: 'block', p: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {attachment.file_name}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );

  if (loading && id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <CircularProgress sx={{ color: '#008080' }} />
      </Box>
    );
  }

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={handleBack}>
          <ArrowBackIosIcon />
        </IconButton>
        <Typography variant="h6">
          {id ? 'Edit Opportunity' : 'Create Opportunity'}
        </Typography>
      </Box>

      <Paper sx={{ padding: 3 }}>
        <Grid container spacing={2}>

          {/* Basic Information */}

          <Grid item xs={12} md={4}>
            <Autocomplete
              freeSolo
              options={[...opportunityNameTags, { tag_text: 'custom', isCustom: true }]}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.tag_text}
              isOptionEqualToValue={(option, value) => {
                if (typeof value === 'string') return option.tag_text === value;
                return option.tag_text === value?.tag_text;
              }}
              value={form.opportunity_name}
              onChange={(event, newValue) => {
                if (typeof newValue === 'object' && newValue?.isCustom) {
                  handleOpenAddNameDialog();
                } else {
                  const val = typeof newValue === 'string' ? newValue : newValue?.tag_text || '';
                  const key = typeof newValue === 'object' ? newValue?.tag_key : '';
                  setForm(prev => ({ 
                    ...prev, 
                    opportunity_name: val,
                    opportunity_name_key: key || prev.opportunity_name_key 
                  }));
                }
              }}
              onInputChange={(event, newInputValue) => {
                handleChange('opportunity_name', newInputValue);
              }}
              renderOption={(props, option) => {
                if (option.isCustom) {
                  return (
                    <li {...props} style={{ justifyContent: 'center', padding: '0px' }}>
                      <Button 
                        fullWidth 
                        variant="text" 
                        startIcon={<AddIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenAddNameDialog();
                        }}
                        sx={{ color: '#008080', px: 2, justifyContent: 'flex-start' }}
                      >
                        Add Opportunity Name
                      </Button>
                    </li>
                  );
                }
                return <li {...props}>{option.tag_text}</li>;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Opportunity Name *"
                  error={!!errors.opportunity_name}
                  helperText={errors.opportunity_name}
                  sx={themedTextFieldSx}
                />
              )}
              sx={themedAutocompleteSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth error={!!errors.segment}>
              <InputLabel sx={inputLabelFocusedStyle}>Segment *</InputLabel>
              <Select
                value={form.segment}
                label="Segment *"
                onChange={(e) => handleChange('segment', e.target.value)}
                sx={selectFocusedStyle}
              >
                {segmentOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
              {errors.segment && <Typography color="error" variant="caption">{errors.segment}</Typography>}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel sx={inputLabelFocusedStyle}>Location</InputLabel>
              <Select
                value={form.location_id}
                label="Location"
                onChange={(e) => handleChange('location_id', e.target.value)}
                sx={selectFocusedStyle}
              >
                {locations.map(location => (
                  <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              options={greenUsers.filter(u => !form.bd_owners.some(selected => selected.id === u.id))}
              getOptionLabel={(option) => option.name ? (option.user_id ? `${option.name} (${option.user_id})` : option.name) : ''}
              value={form.bd_owners}
              onChange={(e, newValue) => handleChange('bd_owners', newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name ? (option.user_id ? `${option.name} (${option.user_id})` : option.name) : ''}
                    {...getTagProps({ index })}
                    key={index}
                    size="small"
                    sx={{ backgroundColor: '#008080', color: '#fff', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="BD Owner / SPOC *"
                  error={!!errors.bd_owners}
                  helperText={errors.bd_owners}
                  sx={themedAutocompleteSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description of the Opportunity"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              sx={themedTextFieldSx}
            />
          </Grid>

          {/* Stage 1: Qualified Lead */}
          <Grid item xs={12}>
            <Accordion sx={{ border: '1px solid #e0e0e0', boxShadow: 'none' }} defaultExpanded={!!id && attachments.qualified_lead.length > 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#008080' }} />} sx={{ bgcolor: '#f5fafa' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Stage 1 - Qualified Lead</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={3}>
                    <ThemedDatePicker
                      label="Date"
                      value={form.qualified_lead_date}
                      onChange={(val) => handleChange('qualified_lead_date', val)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel sx={inputLabelFocusedStyle}>Type</InputLabel>
                      <Select
                        value={form.qualified_lead_type}
                        label="Type"
                        onChange={(e) => handleChange('qualified_lead_type', e.target.value)}
                        sx={selectFocusedStyle}
                      >
                        {qualifiedLeadTypes.map(type => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Comment"
                      value={form.qualified_lead_comment}
                      onChange={(e) => handleChange('qualified_lead_comment', e.target.value)}
                      sx={themedTextFieldSx}
                    />
                  </Grid>
                  <FileUploadSection stage="qualified_lead" />
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Stage 2: Feasibility */}
          <Grid item xs={12}>
            <Accordion sx={{ border: '1px solid #e0e0e0', boxShadow: 'none' }} defaultExpanded={!!id && attachments.feasibility.length > 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#008080' }} />} sx={{ bgcolor: '#f5fafa' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Stage 2 - Feasibility</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={3}>
                    <ThemedDatePicker
                      label="Date"
                      value={form.feasibility_date}
                      onChange={(val) => handleChange('feasibility_date', val)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Visit Report"
                      value={form.feasibility_visit_report}
                      onChange={(e) => handleChange('feasibility_visit_report', e.target.value)}
                      sx={themedTextFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Comment"
                      value={form.feasibility_comment}
                      onChange={(e) => handleChange('feasibility_comment', e.target.value)}
                      sx={themedTextFieldSx}
                    />
                  </Grid>
                  <FileUploadSection stage="feasibility" />
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Stage 3: Proposal */}
          <Grid item xs={12}>
            <Accordion sx={{ border: '1px solid #e0e0e0', boxShadow: 'none' }} defaultExpanded={!!id && attachments.proposal.length > 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#008080' }} />} sx={{ bgcolor: '#f5fafa' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Stage 3 - Proposal</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} alignItems="flex-start">
                  {proposals.map((proposal, index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={12} md={2}>
                        <ThemedDatePicker
                          label="Proposal Date"
                          value={proposal.proposal_date}
                          onChange={(val) => updateProposal(index, 'proposal_date', val)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Proposal Value *"
                          value={proposal.proposal_value}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            updateProposal(index, 'proposal_value', val);
                          }}
                          error={!!(errors.proposals && errors.proposals[index]?.proposal_value)}
                          helperText={errors.proposals && errors.proposals[index]?.proposal_value}
                          sx={themedTextFieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Proposal Type"
                          value={proposal.proposal_type}
                          onChange={(e) => updateProposal(index, 'proposal_type', e.target.value)}
                          sx={themedTextFieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel sx={{ ...inputLabelFocusedStyle, maxWidth: 'calc(100% - 32px)' }}>Mode of Communication</InputLabel>
                          <Select
                            value={proposal.mode_of_communication}
                            label="Mode of Communication"
                            onChange={(e) => updateProposal(index, 'mode_of_communication', e.target.value)}
                            sx={selectFocusedStyle}
                            MenuProps={{ disableScrollLock: true, disablePortal: true }}
                          >
                            {modeOfCommunicationOptions.map(option => (
                              <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                        {proposals.length > 1 && (
                          <IconButton color="error" onClick={() => removeProposal(index)}>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Proposal Comment"
                          multiline
                          rows={2}
                          value={proposal.comment}
                          onChange={(e) => updateProposal(index, 'comment', e.target.value)}
                          sx={themedTextFieldSx}
                        />
                      </Grid>
                    </React.Fragment>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addProposal}
                      variant="outlined"
                      size="small"
                      sx={buttonStyles.outlined}
                    >
                      Add Proposal
                    </Button>
                  </Grid>
                  <FileUploadSection stage="proposal" />
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Stage 4: Negotiation */}
          <Grid item xs={12}>
            <Accordion sx={{ border: '1px solid #e0e0e0', boxShadow: 'none' }} defaultExpanded={!!id && attachments.negotiation.length > 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#008080' }} />} sx={{ bgcolor: '#f5fafa' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Stage 4 - Negotiation</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} alignItems="flex-start">
                  {negotiations.map((negotiation, index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={12} md={3}>
                        <ThemedDatePicker
                          label="Negotiation Date"
                          value={negotiation.negotiation_date}
                          onChange={(val) => updateNegotiation(index, 'negotiation_date', val)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Negotiated Value *"
                          value={negotiation.negotiated_value}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            updateNegotiation(index, 'negotiated_value', val);
                          }}
                          error={!!(errors.negotiations && errors.negotiations[index]?.negotiated_value)}
                          helperText={errors.negotiations && errors.negotiations[index]?.negotiated_value}
                          sx={themedTextFieldSx}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel sx={{ ...inputLabelFocusedStyle, maxWidth: 'calc(100% - 32px)' }}>Mode of Communication</InputLabel>
                          <Select
                            value={negotiation.negotiation_mode_of_communication}
                            label="Mode of Communication"
                            onChange={(e) => updateNegotiation(index, 'negotiation_mode_of_communication', e.target.value)}
                            sx={selectFocusedStyle}
                            MenuProps={{ disableScrollLock: true, disablePortal: true }}
                          >
                            {modeOfCommunicationOptions.map(option => (
                              <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                        {negotiations.length > 1 && (
                          <IconButton color="error" onClick={() => removeNegotiation(index)}>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Negotiation Comment"
                          value={negotiation.comment}
                          onChange={(e) => updateNegotiation(index, 'comment', e.target.value)}
                          sx={themedTextFieldSx}
                        />
                      </Grid>
                    </React.Fragment>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addNegotiation}
                      variant="outlined"
                      size="small"
                      sx={buttonStyles.outlined}
                    >
                      Add Negotiation
                    </Button>
                  </Grid>
                  <FileUploadSection stage="negotiation" />
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Stage 5: Won/Lost */}
          <Grid item xs={12}>
            <Accordion sx={{ border: '1px solid #e0e0e0', boxShadow: 'none' }} defaultExpanded={!!id && attachments.won_lost.length > 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#008080' }} />} sx={{ bgcolor: '#f5fafa' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Stage 5 - Won / Lost</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel sx={inputLabelFocusedStyle}>Status</InputLabel>
                      <Select
                        value={form.won_lost_status}
                        label="Status"
                        onChange={(e) => handleChange('won_lost_status', e.target.value)}
                        sx={selectFocusedStyle}
                        MenuProps={{ disableScrollLock: true }}
                      >
                        <MenuItem value="Won">Won</MenuItem>
                        <MenuItem value="Lost">Lost</MenuItem>
                        <MenuItem value="Hold">Hold</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {form.won_lost_status && (
                    <Grid item xs={12} md={3}>
                      <ThemedDatePicker
                        label="Date"
                        value={form.won_lost_date}
                        onChange={(val) => handleChange('won_lost_date', val)}
                      />
                    </Grid>
                  )}
                  {form.won_lost_status === 'Won' && (
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="PO Value (Ex GST)"
                        value={form.po_value_ex_gst}
                        onChange={(e) => handleChange('po_value_ex_gst', e.target.value)}
                        sx={themedTextFieldSx}
                      />
                    </Grid>
                  )}
                  {form.won_lost_status && (
                    <Grid item xs={12} md={form.won_lost_status === 'Won' ? 3 : 6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Comment"
                        value={form.won_lost_comment}
                        onChange={(e) => handleChange('won_lost_comment', e.target.value)}
                        sx={themedTextFieldSx}
                      />
                    </Grid>
                  )}
                  <FileUploadSection stage="won_lost" />
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Submit Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
              <Button variant="outlined" onClick={handleBack} sx={buttonStyles.outlined}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={buttonStyles.contained}
              >
                {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : (id ? 'Update' : 'Submit')}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Paper>

      {/* Preview Modal */}
      <Dialog open={previewModal.open} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{previewModal.attachment?.file_name}</Typography>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f5f5f5' }}>
          {previewModal.attachment && (
            previewModal.attachment.file_type?.startsWith('image/') ? (
              <img 
                src={previewModal.attachment.preview || previewModal.attachment.file_path} 
                alt={previewModal.attachment.file_name} 
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
              />
            ) : previewModal.attachment.file_type === 'application/pdf' ? (
              <iframe 
                src={previewModal.attachment.preview || previewModal.attachment.file_path} 
                title={previewModal.attachment.file_name}
                width="100%" 
                height="100%" 
                style={{ border: 'none' }} 
              />
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <InsertDriveFileIcon sx={{ fontSize: 100, color: '#666', mb: 2 }} />
                <Typography>Preview not available for this file type.</Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }} 
                  href={previewModal.attachment.preview || previewModal.attachment.file_path} 
                  target="_blank"
                  download
                >
                  Download File
                </Button>
              </Box>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Add Opportunity Name Dialog */}
      <Dialog open={openAddNameDialog} onClose={handleCloseAddNameDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600, color: '#008080' }}>Add Opportunity Name</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Tag Key (e.g. OPP_NAME)"
              value={customNameKey}
              onChange={(e) => setCustomNameKey(e.target.value)}
              sx={{ ...themedTextFieldSx, mb: 2 }}
            />
            <TextField
              fullWidth
              label="Opportunity Name"
              value={customNameText}
              onChange={(e) => setCustomNameText(e.target.value)}
              sx={themedTextFieldSx}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddNameDialog} sx={{ color: 'grey.600' }}>Cancel</Button>
          <Button 
            onClick={handleAddCustomName} 
            variant="contained" 
            sx={{ 
              bgcolor: '#008080', 
              '&:hover': { bgcolor: '#006666' } 
            }}
          >
            Add Name
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
